import { useAuth } from "@grafit/frontend-auth/use-auth";
import { Trans } from "@grafit/i18n";
import { lazy, Suspense } from "react";
import { getAppOutdatedState } from "../features/app-update/app-update";
import { AppUpdatePage } from "../features/app-update/app-update-page";
import { NotFoundPage } from "../pages/not-found-page";

const BasicErrorPage = () => (
  <main className="flex min-h-svh flex-col items-center justify-center gap-4 p-4 text-center">
    <h1 className="typo-title-lg-semibold">
      <Trans i18nKey="root.error.fallback.title" />
    </h1>
    <p className="typo-body-base-regular">
      <Trans i18nKey="root.error.fallback.description" />
    </p>
    <a className="typo-body-base-semibold underline" href="/">
      <Trans i18nKey="root.error.fallback.cta" />
    </a>
  </main>
);

const ErrorPage = lazy(() =>
  import("../pages/error-page/error-page").then(
    (module) => ({ default: module.ErrorPage }),
    () => ({ default: BasicErrorPage }),
  ),
);
interface RootErrorFallbackProps {
  error?: unknown;
  canContactSupport?: boolean;
}

export const RootErrorFallbackWithAuth = ({ error }: Pick<RootErrorFallbackProps, "error">) => {
  const auth = useAuth();

  return <RootErrorFallback error={error} canContactSupport={!!auth.user} />;
};

export const RootErrorFallback = ({ error, canContactSupport = false }: RootErrorFallbackProps) => {
  const isAppOutdated = getAppOutdatedState();

  if (isAppOutdated) {
    return <AppUpdatePage />;
  }

  if (error instanceof Object && "data" in error && error.data instanceof Object && "httpStatus" in error.data) {
    if (error.data.httpStatus === 404) {
      return <NotFoundPage />;
    }
  }

  return (
    <Suspense fallback={<BasicErrorPage />}>
      <ErrorPage canContactSupport={canContactSupport} />
    </Suspense>
  );
};
